import { ref, readonly, inject, ssrContextKey, watchEffect, isReactive, toRaw, defineComponent, reactive, onServerPrefetch, provide, nextTick, resolveComponent, openBlock, createBlock, renderSlot, createVNode, computed, resolveDynamicComponent, withCtx, onMounted, watch, onUnmounted, createCommentVNode } from 'vue';
import consola from 'consola';
import Cache from '@drapejs/cache';
import createInvoker, { defineCommand } from '@drapejs/invoker';

const meta$1 = ref(readonly({}));
let watchHandler;
function setMeta(setter) {
  const ssrContext = inject(ssrContextKey, undefined);
  if (ssrContext) {
    ssrContext.drapejs__meta = setter;
    return;
  }
  if (typeof document === 'undefined') {
    return;
  }
  if (watchHandler) {
    watchHandler();
  }
  watchHandler = watchEffect(() => {
    meta$1.value = readonly(setter() || {});
    if (!meta$1.value) {
      return;
    }
    document.title = meta$1.value.title || '';
    if (meta$1.value.lang) {
      document.documentElement.lang = meta$1.value.lang;
    }
    let expectedProps = {
      ...meta$1.value.props
    };
    document.querySelectorAll("meta[name]").forEach(el => {
      const name = el.getAttribute('name');
      const dataDjsValue = el.getAttribute('data-djs');
      if (dataDjsValue && name && !expectedProps[name]) {
        if (dataDjsValue === 'data-djs') {
          consola.trace('Remove meta prop', name);
          el.remove();
        } else {
          consola.trace('Reset meta prop', name);
          el.setAttribute('content', el.getAttribute('data-djs'));
          el.removeAttribute('data-djs');
        }
        delete expectedProps[name];
        return;
      }
      if (!el.getAttribute('data-djs') && name && expectedProps[name]) {
        // Store default value
        el.setAttribute('data-djs', el.getAttribute('content'));
      }
      if (name && expectedProps[name]) {
        consola.trace('Set meta prop', name, expectedProps[name]);
        el.setAttribute('content', expectedProps[name]);
        delete expectedProps[name];
      }
    });
    for (const key in expectedProps) {
      consola.trace('Adding meta prop', key, expectedProps[key]);
      const el = document.createElement('meta');
      el.setAttribute('name', key);
      el.setAttribute('content', expectedProps[key]);
      el.setAttribute('data-djs', 'data-djs');
      document.head.appendChild(el);
    }
    let expectedLinks;
    if (!Array.isArray(meta$1.value.links)) {
      expectedLinks = {};
    } else {
      expectedLinks = meta$1.value.links.reduce((res, i) => {
        if (i.rel && i.href) {
          res[`${i.rel}${i.href}${i.type ? i.type : ''}`] = i;
        }
        return res;
      }, {});
    }
    function setLinkAttributes(data, el) {
      for (const attr in data) {
        if (attr !== 'exists') {
          el.setAttribute(attr, data[attr]);
        }
      }
      el.setAttribute('data-djs', 'data-djs');
    }
    document.querySelectorAll("link").forEach(el => {
      if (!el.getAttribute('data-djs')) {
        return;
      }
      const type = el.getAttribute('type');
      const existing = expectedLinks[`${el.getAttribute('rel')}${el.getAttribute('href')}${type ? type : ''}`];
      if (existing) {
        existing.exists = true;
        setLinkAttributes(existing, el);
      } else {
        consola.trace('Removing meta link', el);
        el.remove();
      }
    });
    for (const key in expectedLinks) {
      const l = expectedLinks[key];
      if (!l.exists) {
        consola.trace('Adding meta link', l);
        const el = document.createElement('link');
        setLinkAttributes(l, el);
        document.head.appendChild(el);
      }
    }
  });
  let expectedScripts;
  if (!Array.isArray(meta$1.value.scripts)) {
    expectedScripts = {};
  } else {
    expectedScripts = meta$1.value.scripts.reduce((res, s) => {
      if (s.id && (s.src || s.inline)) {
        res[s.id] = s;
      }
      return res;
    }, {});
  }
  function setScriptAttributes(data, el) {
    for (const attr in data) {
      if (['exists', 'inline'].indexOf(attr) === -1) {
        el.setAttribute(attr, data[attr]);
      }
    }
    if (data.inline) {
      el.innerHTML = '';
      let inlineScript = document.createTextNode(data.inline);
      el.appendChild(inlineScript);
    }
    el.setAttribute('data-djs', 'data-djs');
  }
  document.querySelectorAll("script").forEach(el => {
    if (!el.getAttribute('data-djs')) {
      return;
    }
    const existing = expectedScripts[`${el.getAttribute('id')}`];
    if (existing) {
      existing.exists = true;
      setScriptAttributes(existing, el);
    } else {
      consola.trace('Removing meta script', el);
      el.remove();
    }
  });
  for (const key in expectedScripts) {
    const l = expectedScripts[key];
    if (!l.exists) {
      consola.trace('Adding meta script', l);
      const el = document.createElement('script');
      setScriptAttributes(l, el);
      document.head.appendChild(el);
    }
  }
}

/** Function to programmatically trigger a router navigation */
const navigateKey = Symbol('navigate');
const prefetchKey = Symbol('prefetch');
/** Provides access to current route info */
const routeKey = Symbol('route');
/**
 * A number representing the number of receivers has run for the current fetchPage command.
 *
 * Is null if there's no loading in progress.
 */
const loadingProgressKey = Symbol('loadingProgress');
/** Provides access to the current page's data */
const pageKey = Symbol('page');
/**
 * Subscribe/Listen for changes to an item in the Cache. The item must not exist yet when this is called. The subscription will still fire once the item is created.
 */
const subscribeKey = Symbol('subscribe');
/** Remove a subscription in the Cache */
const unsubscribeKey = Symbol('unsubscribe');
/** Sets an item to the app's Cache. If called on an SSR request, the cache will be transparently moved to the client and be available using getItemKey or getServerItemKey */
const setItemKey = Symbol('setItem');
/** Sets many items to the app's Cache. If called on an SSR request, the cache will be transparently moved to the client and be available using getItemKey or getServerItemKey */
const setManyItemsKey = Symbol('setManyItems');
/** Gets an item from the app's Cache */
const getItemKey = Symbol('getItem');
/**
 * If the page originates from an SSR request, this function allows blocking synchronous access to that initial server-side cache state. Useful for rehydrating components in the client since all of Vue's lifecycle hooks ar synchronous.
 *
 * Note that this does not update when the cache is updated during runtime. Use getItemKey instead to get the Cache's current value.
 */
const getServerItemKey = Symbol('getServerItem');
/** Invokes a command on the app's Invoker */
const invokeKey = Symbol('invoke');
const routerIgnoreQueryParamsKey = Symbol('routerIgnoreQueryParams');
const isNavigateDisabledKey = Symbol('isNavigateDisabled');

/**
 * Instructs connectors to load the page for the given path. Is normally used by a router
 * component to fetch the page data needed. Can also be used by other components to pre-fetch
 * data that will be needed later.
 */
const fetchPage = defineCommand("FetchPage");
function fetchPageBuilder(protocol, host, path, query, hash) {
  if (isReactive(query)) {
    query = toRaw(query);
  }
  let cacheKey = path || '';
  var queryItems = [];
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value) {
        queryItems.push(`${key}=${value}`);
      } else {
        queryItems.push(`${key}`);
      }
    }
  }
  if (queryItems.length > 0) {
    cacheKey = `${cacheKey}?${queryItems.join('&')}`;
  }
  return {
    protocol,
    host,
    path,
    query: query || {},
    hash,
    cacheKey
  };
}

/**
 * Extracts the path from a URL
 * @param input A *relative* URL
 * @returns the path of the URL
 */
function getPath(url) {
  let pos = url.indexOf('?');
  if (pos > -1) {
    url = url.substring(0, pos);
  }
  pos = url.indexOf('#');
  if (pos > -1) {
    url = url.substring(0, pos);
  }
  return url;
}
/**
 * Parses a query string into a dictionary
 * @param path An absolute or relative URL
 * @param ignoreList A list of keys to ignore in the querystring
 * @returns a dictionary with query parameters
 */
function getQuery(url) {
  const start = url.indexOf('?');
  const end = url.indexOf('#');
  if (start < 0) {
    return {};
  }
  const str = url.substring(start + 1, end > 0 ? end : undefined);
  let query = {};
  let items = str.split('&');
  for (let i = 0; i < items.length; i++) {
    let [key, value] = items[i].split('=');
    query[decodeURIComponent(key)] = value && decodeURIComponent(value) || null;
  }
  return query;
}
/**
 * Extracts the querystring from a URL
 * @param url An absolute or relative URL
 * @returns the querystring part *excluding* '?'
 */
function getSearch(url) {
  const start = url.indexOf('?');
  const end = url.indexOf('#');
  if (start < 0) {
    return;
  }
  return url.substring(start + 1, end > 0 ? end : undefined);
}
/**
 * Extracts the hash from a URL
 * @param url An absolute or relative URL
 * @returns the hash part *excluding* '#'
 */
function getHash(url) {
  const pos = url.indexOf('#');
  if (pos > -1) {
    return url.substring(pos + 1);
  }
  return;
}

var script$2 = defineComponent({
  name: "RouterView",
  setup() {
    if (typeof history !== 'undefined' && 'scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    const ssrContext = inject(ssrContextKey, undefined);
    const subscribe = inject(subscribeKey, () => new Promise(r => r()));
    const unsubscribe = inject(unsubscribeKey, () => new Promise(r => r()));
    const getItem = inject(getItemKey, () => new Promise(r => r()));
    const invoke = inject(invokeKey, () => new Promise(r => r()));
    const setManyItems = inject(setManyItemsKey, () => new Promise(r => r()));
    const routerIgnoreQueryParams = inject(routerIgnoreQueryParamsKey, {
      value: []
    });
    const isNavigateDisabled = inject(isNavigateDisabledKey, () => false);
    const loadingProgress = ref(null);
    let target = {};
    const currentRoute = reactive({
      href: '',
      query: {},
      id: 0,
      replaceHistory: false
    });
    const page = ref({});
    loadSSRState();
    loadingProgress.value = 0;
    if (!ssrContext) {
      navigate(window.location.pathname + window.location.search + window.location.hash);
    }
    onServerPrefetch(async () => {
      await navigateSSR(ssrContext.req.url);
    });
    provide(navigateKey, navigate);
    provide(prefetchKey, prefetch);
    provide(routeKey, readonly(currentRoute));
    provide(loadingProgressKey, readonly(loadingProgress));
    provide(pageKey, readonly(page));
    return {
      currentRoute
    };

    /**
     * Performs an asynchronous fetchPage, trying to load previously cached data if available
     */
    async function navigate(path, options) {
      if (isNavigateDisabled()) {
        return;
      }
      path = decodeURI(path);
      try {
        if (!path) {
          path = (target.pathname || '') + target.search + target.hash;
        }
        if (!options) {
          options = {
            replaceHistory: false
          };
        }
        unsubscribeCurrent();
        const query = getQuery(path);
        target.protocol = window.location.protocol;
        target.host = window.location.host;
        target.pathname = getPath(path);
        target.search = getSearch(path);
        target.query = query;
        target.hash = getHash(path);
        target.replaceHistory = options.replaceHistory || false;
        target.loaded = false;
        if (!currentRoute.href) {
          // Set default route value to make it immediately avaialable to child components.
          // Normally route is changed first after data is fetched from back-end.
          setCurrentRoute({
            component: '',
            href: `${target.protocol}//${target.host}${target.pathname}${target.search ? '?' + target.search : ''}${target.hash ? '#' + target.hash : ''}`,
            protocol: target.protocol,
            host: target.host,
            pathname: target.pathname,
            query: target.query,
            search: target.search,
            hash: target.hash,
            statusCode: 0,
            replaceHistory: false,
            id: 0
          });
        }
        if (isTargetDifferentFromCurrent()) {
          loadingProgress.value = 0;
        }
        const payload = getPayloadAndUpdateCacheKey(target);
        await subscribeCurrent();
        try {
          await invoke(fetchPage, payload);
        } catch (e) {
          consola.error(e);
          currentRoute.component = 'page-error';
        }
      } finally {
        loadingProgress.value = null;
      }
    }
    async function prefetch(path) {
      path = decodeURI(path);
      const query = getQuery(path);
      const prefetchTarget = {
        protocol: window.location.protocol,
        host: window.location.host,
        pathname: getPath(path),
        search: getSearch(path),
        query: query,
        hash: getHash(path),
        replaceHistory: false,
        loaded: false,
        cacheKey: ''
      };
      const payload = getPayloadAndUpdateCacheKey(prefetchTarget);
      if (await getItem(payload.cacheKey)) {
        console.log("-----> den är cachad <-------");
        return;
      }
      try {
        await invoke(fetchPage, payload);
      } catch (e) {
        consola.error(e);
      }
    }
    /**
     * Performs a synchronous fetchPage
     */
    async function navigateSSR(path) {
      path = decodeURI(path);
      const query = getQuery(path);
      // console.log("på servern, ssrContext: " + JSON.stringify(ssrContext));

      target.protocol = ssrContext.req.protocol;
      if (!target.protocol.endsWith(':')) {
        target.protocol = target.protocol + ':';
      }
      target.host = ssrContext.req.hostname;
      target.pathname = getPath(path);
      target.search = getSearch(path);
      target.query = query;
      target.hash = getHash(path);
      target.loaded = false;
      const payload = getPayloadAndUpdateCacheKey(target);
      target.cacheKey = payload.cacheKey;

      // This is "hidden" property is picked up by server-renderer
      ssrContext.drapejs__target = target;
      await invoke(fetchPage, payload);
      const result = await getItem(target.cacheKey + ":res");
      const headers = Object.fromEntries(result?.headers ?? {});
      setPageState(await getItem(target.cacheKey), headers);
    }
    function getPayloadAndUpdateCacheKey(target) {
      const filteredQuery = Object.keys(target.query).reduce((res, key) => {
        if (!routerIgnoreQueryParams || !routerIgnoreQueryParams.find(k => k.test(key))) {
          res[key] = target.query[key];
        }
        return res;
      }, {});
      const filtered = fetchPageBuilder(target.protocol, target.host, target.pathname, filteredQuery, target.hash);
      const result = fetchPageBuilder(target.protocol, target.host, target.pathname, target.query, target.hash);
      result.cacheKey = filtered.cacheKey;
      target.cacheKey = filtered.cacheKey;
      target.headers = ssrContext?.req?.headers;
      result.headers = ssrContext?.req?.headers;
      return result;
    }
    function setPageState(data, headers) {
      // consola.trace('drapeJS: setPageState called with data ' + JSON.stringify(data));
      let key = null;
      if (!data) {
        data = {};
      }
      data.headers = headers ?? {};
      if (data.status && !data.http_status) {
        consola.warn("Page data contains 'status' property which is deprecated - use 'http_status' instead.");
        data.http_status = data.status;
      }
      if (data.statusCode && !data.http_status) {
        consola.warn("Page data contains 'statusCode' property which is deprecated - use 'http_status' instead.");
        data.http_status = data.statusCode;
      }
      let http_location = undefined;
      if (data.http_status >= 300 && data.http_status <= 399 && data.http_location) {
        if (typeof window !== 'undefined') {
          if (data.http_location.indexOf('/') === 0 || data.http_location.indexOf(`${target.protocol}//${target.host}`) === 0) {
            // "Local" redirect
            navigate(data.http_location.replace(`${target.protocol}//${target.host}`, ''), {
              replaceHistory: true
            });
          } else {
            // External redirect
            window.location = data.http_location;
          }
          return;
        }
        // The key doesn't really matter, since the server host should return location headers,
        // but we use error here as fall back if the server isn't configured properly.
        key = 'page-error';
        http_location = data.http_location;
      } else if (data.http_status >= 400 && data.http_status !== 404 && data.http_status !== 401) {
        key = 'page-error';
      } else if (!data.type || data.http_status == 404) {
        key = 'page-not-found';
        data.http_status = 404;
      } else if (data.http_status == 401) {
        key = 'page-forbidden';
      } else {
        // TODO Check if page component exists
        key = `page-${data.type.replace(/.([A-Z])/, m => `${m[0]}-${m[1]}`)}`.toLowerCase();
      }
      if (!target.loaded) {
        setCurrentRoute({
          component: key,
          href: `${target.protocol}//${target.host}${target.pathname}${target.search ? '?' + target.search : ''}${target.hash ? '#' + target.hash : ''}`,
          protocol: target.protocol,
          host: target.host,
          pathname: target.pathname,
          query: target.query,
          search: target.search,
          hash: target.hash,
          statusCode: data?.http_status || 200,
          location: http_location,
          replaceHistory: target.replaceHistory,
          id: (currentRoute.id || 0) + 1
        });
        target.loaded = true;
      }
      page.value = data || {};
      if (ssrContext) {
        // These "hidden" properties are picked up by server-renderer
        ssrContext.drapejs__currentRoute = currentRoute;
        ssrContext.drapejs__page = page.value;
      }
      if (target.hash) {
        nextTick(() => {
          /* Look for given anchor and scroll to it if found. We will keep doing this on every
          * page data update as data can come in partial bits.
          */
          consola.trace(`Looking for element with ID ${target.hash}`);
          var elem = document.getElementById(target.hash);
          if (elem) {
            // Found element, clear hash to prevent scrolling on next data update
            target.hash = undefined;
            elem.scrollIntoView();
          }
        });
      }
      if (typeof loadingProgress.value === 'number') {
        loadingProgress.value += 1;
      }
    }
    /**
     * Copies all values to the reactive currentRoute constant
     */
    function setCurrentRoute(value) {
      // consola.trace('drapeJS: setCurrentRoute called');
      currentRoute.component = value.component;
      currentRoute.href = value.href;
      currentRoute.protocol = value.protocol;
      currentRoute.host = value.host;
      currentRoute.pathname = value.pathname;
      currentRoute.query = value.query;
      currentRoute.search = value.search;
      currentRoute.hash = value.hash;
      currentRoute.statusCode = value.statusCode;
      currentRoute.location = value.location;
      currentRoute.replaceHistory = value.replaceHistory;
      currentRoute.id = value.id;
    }
    function isTargetDifferentFromCurrent() {
      return target.pathname !== currentRoute.pathname || target.search !== currentRoute.search;
    }
    async function subscribeCurrent() {
      if (!target.cacheKey) return;
      await subscribe(target.cacheKey, setPageState, {
        immediate: true
      });
    }
    function unsubscribeCurrent() {
      if (!target.cacheKey) return;
      unsubscribe(target.cacheKey, setPageState);
    }
    function loadSSRState() {
      if (!ssrContext && window.__DRAPEJS_STATE__) {
        target = window.__DRAPEJS_STATE__.target;
        setManyItems(window.__DRAPEJS_STATE__.cache);
        setCurrentRoute(window.__DRAPEJS_STATE__.currentRoute);
        page.value = window.__DRAPEJS_STATE__.page;
      }
    }
  }
});

function render$2(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_router_page = resolveComponent("router-page");
  return _ctx.currentRoute.component == 'page-error' ? (openBlock(), createBlock(_component_router_page, {
    key: 0
  })) : renderSlot(_ctx.$slots, "default", {
    key: 1
  }, () => [createVNode(_component_router_page)]);
}

script$2.render = render$2;

const nestedKey = Symbol("RouterLinkNested");
var script$1 = defineComponent({
  name: 'RouterLink',
  props: {
    to: {
      type: String || undefined,
      required: false
    },
    target: {
      type: String,
      default: '_self'
    },
    replaceHistory: {
      type: Boolean,
      default: false
    },
    prefetch: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const navigate = inject(navigateKey);
    const prefetch = inject(prefetchKey);
    const route = inject(routeKey);
    const isNested = inject(nestedKey, false);
    provide(nestedKey, true);
    const toClean = computed(() => {
      if (!props.to) {
        return '';
      }
      if (typeof props.to !== 'string') {
        consola.warn("The supplied URL must be a string! ", props);
        return '';
      }
      let currentHost = `${route?.protocol}//${route?.host}/`;
      if (props.to.indexOf('://') > 0 && props.to.indexOf(currentHost) === 0) {
        return '/' + props.to.substring(currentHost.length);
      }
      return props.to;
    });
    const ignore = computed(() => props.target === '_blank' || toClean.value.indexOf('mailto:') === 0 || toClean.value.indexOf('://') >= 0);
    function onClick(e) {
      if (!ignore.value) {
        e.preventDefault();
        if (isNested) {
          e.stopPropagation();
        }
        navigate?.(toClean.value, {
          replaceHistory: props.replaceHistory
        });
      }
    }
    function performPrefetch() {
      if (!props.prefetch) {
        return;
      }
      prefetch?.(toClean.value);
    }
    return {
      onClick,
      toClean,
      performPrefetch,
      element: isNested ? 'span' : 'a'
    };
  }
});

function render$1(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createBlock(resolveDynamicComponent(_ctx.element), {
    href: _ctx.toClean,
    onClick: _ctx.onClick,
    target: _ctx.target,
    onMouseenter: _cache[0] || (_cache[0] = $event => _ctx.performPrefetch())
  }, {
    default: withCtx(() => [renderSlot(_ctx.$slots, "default")]),
    _: 3
  }, 40, ["href", "onClick", "target"]);
}

script$1.render = render$1;

const meta = ref(null);

/*
The popstate event can not access and store scroll state in the previous event.
This makes it impossible to save the state in the previous history entry before
the pop occurs. This means that going back then forward will not be able to
fetch the previous scroll state upon forward.

To ease this situation, we keep an in-memory cache of scroll state. This breaks
upon full page reload but is deemed a good enough situation until browser APIs
allow us to do something better.
*/
let currentHistoryId = 0;
let nextHistoryId = 1;
const scrollState = {};
var script = defineComponent({
  name: 'RouterPage',
  setup() {
    const navigate = inject(navigateKey);
    const currentRoute = inject(routeKey);
    const loadingProgress = inject(loadingProgressKey, ref(null));
    let previousPath = currentRoute.pathname;
    let isPopping = false;
    let scrollTarget = null;
    onMounted(() => {
      scrollTarget = window.history.state;
      watch(() => currentRoute.href, (newValue, oldValue) => {
        if (!oldValue || isPopping) {
          previousPath = currentRoute.pathname;
          isPopping = false;
          return;
        }
        scrollTarget = null;
        saveScrollPosition();
        consola.trace(`drapeJS: Page changed from ${oldValue} (${document.title}) to ${newValue}`);
        if (currentRoute.replaceHistory) {
          history.replaceState({
            historyId: nextHistoryId
          }, document.title, newValue);
        } else {
          history.pushState({
            historyId: nextHistoryId
          }, document.title, newValue);
          if (currentRoute.pathname != previousPath) {
            consola.trace('drapeJS: Scrolling to top..');
            window.scrollTo(0, 0);
          }
        }
        currentHistoryId = nextHistoryId;
        nextHistoryId += 1;
        previousPath = currentRoute.pathname;
      });
      watch(() => loadingProgress.value, val => {
        if (typeof loadingProgress.value !== 'number') {
          consola.trace('drapeJS: ### PAGE LOADED ###');
        }
        if (typeof val !== 'number' || val === 0) {
          return;
        }
        nextTick(() => {
          if (scrollTarget && typeof scrollTarget.scrollX === 'number' && typeof scrollTarget.scrollY === 'number') {
            consola.trace('drapeJS: Restoring scroll position', scrollTarget);
            window.scrollTo(scrollTarget.scrollX, scrollTarget.scrollY);
            saveScrollPosition();
          }
        });
      });
      window.addEventListener('beforeunload', saveScrollPosition);
      window.addEventListener('popstate', onPopState);
    });
    onUnmounted(() => {
      window.removeEventListener('beforeunload', saveScrollPosition);
      window.removeEventListener('popstate', onPopState);
    });
    return {
      loadingProgress,
      componentName: computed(() => currentRoute.component)
    };
    function saveScrollPosition() {
      if (typeof history !== 'undefined') {
        const state = {
          historyId: currentHistoryId,
          scrollX: window.scrollX,
          scrollY: window.scrollY
        };
        consola.trace('drapeJS: Saving scroll position', state);
        history.replaceState(state, document.title);
        scrollState[currentHistoryId] = state;
      }
    }
    function onPopState(event) {
      // Do not include hash below, as we want the browser to remember it's last scroll position and don't override it.
      let newPath = window.location.pathname + window.location.search;
      let oldPath = currentRoute.pathname + (currentRoute.search ? currentRoute.search : '');
      consola.trace(`drapeJS: Popped state from ${oldPath} to ${newPath}`, event);
      scrollState[currentHistoryId] = {
        scrollX: window.scrollX,
        scrollY: window.scrollY
      };
      currentHistoryId = event.state?.historyId || 0;
      isPopping = true;
      scrollTarget = scrollState[currentHistoryId] ? scrollState[currentHistoryId] : event.state;
      navigate?.(newPath);
    }
  },
  getMeta() {
    return meta.value;
  }
});

function render(_ctx, _cache, $props, $setup, $data, $options) {
  return _ctx.componentName ? (openBlock(), createBlock(resolveDynamicComponent(_ctx.componentName), {
    key: 0,
    ref: "page"
  }, null, 512)) : createCommentVNode("", true);
}

script.render = render;

/**
 * Injects functions to interact with the cache.
 *
 * All injects provide no-op fallbacks to avoid undefined references during runtime.
 */
function useCache () {
  return {
    /**
     * If the page originates from an SSR request, this function allows blocking synchronous access to that initial server-side cache state. Useful for rehydrating components in the client since all of Vue's lifecycle hooks ar synchronous.
     *
     * Note that this does not update when the cache is updated during runtime. Use getItemKey instead to get the Cache's current value.
     */
    getServerItem: inject(getServerItemKey, () => null),
    /**
     * Gets an item from the app's Cache
     */
    getItem: inject(getItemKey, () => Promise.resolve(null)),
    /**
     * Sets an item to the app's Cache. If called on an SSR request, the cache will be transparently moved to the client and be available using getItemKey or getServerItemKey
     */
    setItem: inject(setItemKey, () => Promise.resolve()),
    /**
     * Subscribe/Listen for changes to an item in the Cache. The item must not exist yet when this is called. The subscription will still fire once the item is created.
     */
    subscribe: inject(subscribeKey, () => Promise.resolve()),
    /**
     * Remove a subscription in the Cache
     */
    unsubscribe: inject(unsubscribeKey, () => Promise.resolve())
  };
}

/**
 * Returns a ref that will always contain the specified cache entry.
 *
 * This works syncronous in SSR by using onServerPrefetch on a server request and getServerItem on the client.
 *
 * @param cacheKey The cache entry to bind. Either a static string or dynamic function returning a string that should be bound
 * @param callback Optional callback to call whenever the bound value changes. This is not a deep watch. It's only called if there is an actual value.
 * @returns a reference containing the cache entry, and that updates whenever the cache or cacheKey updates
 */
function bind(cacheKey, callback) {
  const {
    getServerItem,
    getItem,
    subscribe,
    unsubscribe
  } = useCache();
  let key;
  const result = ref(getServerItem(getKey()));
  onServerPrefetch(async () => {
    result.value = await getItem(getKey());
    executeCallback();
  });
  onMounted(async () => {
    watchEffect(async () => {
      const newKey = getKey();
      if (key) {
        await unsubscribe(key, cacheHandler);
      }
      key = newKey;
      console.log(key);
      await subscribe(key, cacheHandler, {
        immediate: true
      });
    });
  });
  onUnmounted(async () => {
    if (key) {
      await unsubscribe(key, cacheHandler);
    }
  });
  return readonly(result);
  function getKey() {
    return typeof cacheKey === 'function' ? cacheKey() : cacheKey;
  }
  function cacheHandler(val) {
    result.value = val;
    executeCallback();
  }
  function executeCallback() {
    if (callback && result.value) {
      callback(result.value);
    }
  }
}

/**
 * This is a base component to simplify working with Vue's options API. It provides easy
 * access to otherwise injected functions and properties. It also provides backward
 * compatibility with earlier versions of drapejs.
 *
 * - $page
 * - $invoke
 */
const OptionsComponent = defineComponent({
  inject: {
    $navigate: {
      from: navigateKey,
      default: undefined
    },
    $invoke: {
      from: invokeKey,
      default: undefined
    },
    $subscribe: {
      from: subscribeKey,
      default: undefined
    },
    $unsubscribe: {
      from: unsubscribeKey,
      default: undefined
    },
    $getItem: {
      from: getItemKey,
      default: undefined
    },
    $setItem: {
      from: setItemKey,
      default: undefined
    },
    $setManyItems: {
      from: setManyItemsKey,
      default: undefined
    },
    $getServerItem: {
      from: getServerItemKey,
      default: undefined
    },
    $page: {
      from: pageKey,
      default: {}
    },
    $route: {
      from: routeKey,
      default: undefined
    },
    $ssrContext: {
      from: ssrContextKey,
      default: undefined
    }
  }
});

console.log('Vue version core: ' + require('vue').version);
console.log('Vue path core: ' + require.resolve('vue'));
/**
 * Create an Invoker with an attached Cache instance.
 * @param worker If set, this invoker instance will proxy all invoke requests to the remove Web Worker. Otherwise they will execute in the currnet thread.
 */
function createInvokerWithCache(worker) {
  const cache = new Cache();
  const invoker = createInvoker(worker);
  invoker.cache = cache;
  return invoker;
}
const install = function installDrapejs(app, options) {
  // Unwrap refs in templates and options API. This is a temporary vue 3.2 config which will be removed when this behavior becomes default in 3.3.
  app.config.unwrapInjectedRef = true;
  if (!options) {
    options = {};
  }
  const routerIgnoreQueryParams = options.routerIgnoreQueryParams ? reactive(options.routerIgnoreQueryParams) : reactive([]);
  if (options.pageComponents) {
    for (const [key, value] of Object.entries(options.pageComponents)) {
      const fullKey = `page-${key.replace(/.([A-Z])/, m => `${m[0]}-${m[1]}`)}`.toLowerCase();
      consola.trace(`Registering page-${fullKey}`);
      app.component(fullKey, value);
    }
  }
  const worker = options.workerFactory?.();
  const invoker = createInvokerWithCache(worker);
  app.invoker = invoker;
  const cache = invoker.cache;
  if (worker) {
    cache.listen(worker);
  }
  app.cache = cache;
  let navigateIsDisabled = false;
  app.provide(isNavigateDisabledKey, () => navigateIsDisabled);
  app.disableNavigate = () => {
    consola.trace('drapeJS: Navigation is disabled');
    navigateIsDisabled = true;
  };
  app.provide(subscribeKey, (key, handler, options) => cache.subscribe(key, handler, options));
  app.provide(unsubscribeKey, (key, handler) => cache.unsubscribe(key, handler));
  app.provide(setItemKey, (key, val) => cache.setItem(key, val));
  app.provide(setManyItemsKey, values => cache.setManyItems(values));
  app.provide(getItemKey, key => cache.getItem(key));
  app.provide(getServerItemKey, getServerItem);
  app.provide(invokeKey, (type, command) => invoker.invoke(type, command));
  app.provide(routerIgnoreQueryParamsKey, routerIgnoreQueryParams);
  app.mixin({
    created() {
      setMeta_legacy.call(this);
    },
    watch: {
      meta() {
        setMeta_legacy.call(this);
      }
    }
  });
  app.$registerRouterIgnoreQueryParams = (...params) => {
    routerIgnoreQueryParams.push(...params);
  };
  app.component('router-view', script$2);
  app.component('router-link', script$1);
  app.component('router-page', script);
};
function setMeta_legacy() {
  // Pass meta up to parent RouterPage. If we're using async components, the page component will be wrapped
  // in an AsyncComponentWrapper, in which case RouterPage will be 2 levels up the tree.
  let setMeta_legacy = null;
  // We're using the in operator here, as checking whether the function exist by accessing it will cause
  // vue to emit warnings during runtime.
  if (this.$parent && 'drapejs__setMeta' in this.$parent) {
    setMeta_legacy = this.$parent.drapejs__setMeta;
  } else if (this.$parent?.$parent && 'drapejs__setMeta' in this.$parent.$parent) {
    setMeta_legacy = this.$parent.$parent.drapejs__setMeta;
  }
  if (this.meta && setMeta_legacy) {
    consola.warn("'meta' property is deprecated - use setMeta function instead");
    setMeta(() => this.meta);
  }
}
function getServerItem(key) {
  if (typeof window === 'undefined') {
    return;
  }
  return window.__DRAPEJS_STATE__?.cache?.[key];
}

export { OptionsComponent, bind, createInvokerWithCache, install as default, fetchPage, fetchPageBuilder, getItemKey, getServerItem, getServerItemKey, invokeKey, isNavigateDisabledKey, loadingProgressKey, meta$1 as meta, navigateKey, pageKey, prefetchKey, routeKey, routerIgnoreQueryParamsKey, setItemKey, setManyItemsKey, setMeta, subscribeKey, unsubscribeKey, useCache };
